/**
 * `UNASSIGN_DAILY` step. Caller picks the workOrderIds; past-date /
 * on-request filtering happens in the pipeline builder. Uses
 * `skipInvalidate:true` on every batch.
 */
import chunk from 'lodash/chunk'

import { err, ok } from 'types/entities/shared/result'

import { extractBackendError } from '../backendError'

import type { RouteId, WorkOrderId } from 'types/entities/shared/ids'
import type { Deps, PipelineStep, UnassignDailyResponse } from 'types/modules/routing'

import type { StepResult } from '../types'

const BATCH_SIZE = 200

export interface UnassignDailyStepArgs {
  readonly routeId: RouteId
  readonly workOrderIds: ReadonlyArray<WorkOrderId>
}

export type UnassignDailyStepValue = {
  readonly stopIds: ReadonlyArray<string>
  readonly batchCount: number
}

export type UnassignDailyStepResult = StepResult<'UNASSIGN_DAILY', UnassignDailyStepValue>

export const makeUnassignDailyStep = (
  args: UnassignDailyStepArgs,
  deps: Deps,
): PipelineStep<'UNASSIGN_DAILY', UnassignDailyStepResult> => {
  const stepId = `unassign-daily:${args.routeId}`
  return {
    id: stepId,
    phase: 'UNASSIGN_DAILY',
    label: 'Failed to unassign stops from daily schedule',
    applyOrder: 100,
    run: async (ctx) => {
      if (args.workOrderIds.length === 0) {
        return ok({
          phase: 'UNASSIGN_DAILY',
          stepId,
          value: { stopIds: [], batchCount: 0 },
        })
      }
      const batches = chunk(args.workOrderIds as readonly string[], BATCH_SIZE)
      const collected: string[] = []
      try {
        for (const batch of batches) {
          const response: UnassignDailyResponse = await deps
            .unassignDaily({
              routeId: args.routeId,
              data: { stopIds: [...batch], versionContext: ctx.versionContext },
              skipInvalidate: true,
            })
            .unwrap()
          if (response?.stopIds) collected.push(...response.stopIds)
        }
        return ok({
          phase: 'UNASSIGN_DAILY',
          stepId,
          value: { stopIds: collected, batchCount: batches.length },
        })
      } catch (cause: unknown) {
        const { status, message } = extractBackendError(cause)
        return err({
          kind: 'BE_REJECTED',
          phase: 'UNASSIGN_DAILY',
          status,
          message,
        })
      }
    },
  }
}
